import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { getReplacements, simulate } from '@/api/mealPlan'
import {
  BaseUserProfile,
  DayOfWeek,
  MealOfDayDivision,
  RecipeIDObject,
  RecommenderSettings,
  ScaledRecipe,
  SearchIngredient,
  UserNutritionalNeeds,
  UserProfile,
  VarietyOptions,
  WeekPlan,
} from '@/api/types'
import { Button } from '@/components/Button'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { Spinner } from '@/components/Spinner'
import MealPlan from './MealPlan'
import WeekMealPlan from './WeekMealPlan'
import { MealStatsScreen } from '../MealStatsScreen/MealStatsScreen'
import { VarietyOptionsUI } from './VarietyOptions/VarietyOptions'
import { CollapsibleSection } from '../../../components/CollapsibleSection/CollapsibleSection'
import { getWeekPlanIngredientsCount } from '../utils'
import { Switch } from '@/components/Switch'
import { computeReplacedMeals } from './utils.weekplan'

export type GetReplacementsFn = (
  mealDivision: MealOfDayDivision,
  dayOfWeek: DayOfWeek,
  recipe: RecipeIDObject,
) => Promise<Array<ScaledRecipe>>

export default function UserMealPlan({
  userProfile,
  userMealPlan,
  planDate,
  householdMembers,
  userNutritionalNeeds,
  recommenderSettings,
  allowSimulate = true,
  lang,
  ingredients,
}: {
  userProfile: UserProfile
  userMealPlan?: WeekPlan
  householdMembers: BaseUserProfile[]
  planDate: string
  userNutritionalNeeds?: UserNutritionalNeeds
  recommenderSettings?: RecommenderSettings
  allowSimulate?: boolean
  lang?: string
  ingredients: Array<SearchIngredient>
}) {
  const { t } = useTranslation()
  const [mealPlan, setMealPlan] = useState<WeekPlan | undefined>(userMealPlan)
  const [isLoadingPlan, setIsLoadingPlan] = useState(false)
  const [view, setView] = useState('WEEK')
  const [varietyOptions, setVarietyOptions] = useState<VarietyOptions>({
    priorityIngredients: [],
    allowedIngredients: [],
    excludedIngredients: [],
    varietyLevel: 'UNLIMITED',
  })
  const [isRecreate, setIsRecreate] = useState<boolean>(false)
  const [variationOptsCollapsed, setVariationOptsCollapsed] = useState(true)
  const previousMealPlanRef = useRef<WeekPlan | undefined>(undefined)
  const [replacedMealIds, setReplacedMealIds] = useState<Record<string, boolean>>({})

  const simulateMealPlan = useCallback(
    (planAction: 'CREATE' | 'RECREATE' | 'REVIEW') => {
      const isReviewOrRecreate = planAction === 'REVIEW' || planAction === 'RECREATE'
      if (isReviewOrRecreate && mealPlan) {
        previousMealPlanRef.current = mealPlan
      } else {
        previousMealPlanRef.current = undefined
        setReplacedMealIds({})
      }

      setIsLoadingPlan(true)
      setMealPlan(undefined)
      setVariationOptsCollapsed(true)

      simulate({
        userProfile,
        startDate: planDate,
        scope: { householdMembers },
        recommenderSettings,
        planAction,
        lang,
        varietyOptions,
        weekPlan: mealPlan,
      })
        .then((mealPlan) => {
          const ingredients = getWeekPlanIngredientsCount(mealPlan)
          const ingredientIds = Array.from(ingredients.map((ing) => ing.id))
          setVarietyOptions((prev) => ({ ...prev, allowedIngredients: ingredientIds }))
          setMealPlan(mealPlan)
          if (isReviewOrRecreate) {
            setReplacedMealIds(computeReplacedMeals(previousMealPlanRef.current, mealPlan))
          } else {
            setReplacedMealIds({})
          }
          previousMealPlanRef.current = undefined
          setIsLoadingPlan(false)
          setIsRecreate(false)
        })
        .catch(() => {
          setIsLoadingPlan(false)

          toast.error('Error creating plan')
        })
    },
    [userProfile, planDate, householdMembers, recommenderSettings, lang, varietyOptions, mealPlan],
  )

  const getReplacementsFn = useCallback(
    (mealDivision: MealOfDayDivision, dayOfWeek: DayOfWeek, recipe: RecipeIDObject) => {
      if (!mealPlan) return Promise.resolve([])
      return getReplacements(
        userProfile,
        mealPlan,
        mealDivision,
        dayOfWeek,
        recipe,
        recommenderSettings,
      )
    },
    [mealPlan, userProfile, recommenderSettings],
  )

  return (
    <>
      <CollapsibleSection
        label={`Meal Plan Variability Options`}
        isOpen={!variationOptsCollapsed}
        onToggle={() => setVariationOptsCollapsed(!variationOptsCollapsed)}>
        <VarietyOptionsUI
          action={mealPlan ? (isRecreate ? 'RECREATE' : 'REVIEW') : 'CREATE'}
          ingredients={ingredients}
          varietyOptions={varietyOptions}
          setVarietyOptions={setVarietyOptions}
        />
      </CollapsibleSection>

      <div className="MealPlan">
        <div>
          <h4>{t('mealPlan.title')}</h4>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '5px',
            }}>
            {mealPlan && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Button
                  variant="negative"
                  onPress={() => {
                    setMealPlan(undefined)
                    setVarietyOptions({
                      ...varietyOptions,
                      allowedIngredients: [],
                      excludedIngredients: [],
                    })
                  }}>
                  {t('mealSimulation.clearPlan')}
                </Button>
              </div>
            )}
            {mealPlan && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Button
                    variant="primary"
                    onPress={() => {
                      simulateMealPlan(isRecreate ? 'RECREATE' : 'REVIEW')
                    }}>
                    {t('mealSimulation.update')}
                  </Button>
                  <Switch
                    isSelected={isRecreate}
                    onChange={() => {
                      setIsRecreate(!isRecreate)
                      setVarietyOptions({
                        ...varietyOptions,
                        allowedIngredients: [],
                      })
                    }}>
                    {t('mealSimulation.regenerate')}
                  </Switch>
                </div>
                <RadioGroup variation="segmented" value={view} onChange={setView}>
                  <Radio key="DAY" value="DAY">
                    Day
                  </Radio>
                  <Radio key="WEEK" value="WEEK">
                    Week
                  </Radio>
                  <Radio key="STATS" value="STATS">
                    Stats
                  </Radio>
                </RadioGroup>
              </>
            )}
          </div>
        </div>
        {!mealPlan ? (
          <div className="mealPlanContainer">
            {isLoadingPlan ? (
              <Spinner />
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '285px',
                    alignItems: 'center',
                    border: '1px dashed #999999',
                    borderRadius: '5px',
                    marginTop: '20px',
                  }}>
                  {allowSimulate ? (
                    <Button variant="primary" onPress={() => simulateMealPlan('CREATE')}>
                      {t('mealPlan.calculate')}
                    </Button>
                  ) : (
                    <div>{t('mealPlan.noPlan')}</div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {view === 'DAY' ? (
              <MealPlan
                weekPlan={mealPlan}
                userNutritionalNeeds={userNutritionalNeeds}
                mainUserId={userProfile.idUser}
                onReplaceMeal={() => {}}
                simulateMealPlan={() => simulateMealPlan('CREATE')}
                getReplacements={getReplacementsFn}
                replacedMealIds={replacedMealIds}
              />
            ) : view === 'WEEK' ? (
              <WeekMealPlan
                weekPlan={mealPlan}
                mainUserId={userProfile.idUser}
                getReplacements={getReplacementsFn}
                replacedMealIds={replacedMealIds}
              />
            ) : (
              <MealStatsScreen
                weekPlan={mealPlan}
                userNutritionalNeeds={userNutritionalNeeds}
                userId={userProfile.idUser}
                priorityIngredients={varietyOptions.priorityIngredients}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
